import { useEffect } from 'react'
import { useParams, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { ListRefreshIndicator } from '@/features/order-confirmation/components/ListRefreshIndicator'
import { workflowListQueryOptions } from '@/features/order-confirmation/workflowListQueries'
import { getWorkflowDone } from '@/services/api'
import { Pagination } from '@/components/ui/Pagination'
import { relativeTime } from '@/lib/relativeTime'
import type { DoneItem } from '@/types/purchaseOrder'
import type { UserReference } from '@/types/orderConfirmation'
import type { OverviewOutletContext } from './OverviewPage'

function validatorName(validator: UserReference) {
  return `${validator.first_name} ${validator.last_name}`.trim() || validator.email_address
}

function Row({ item, onClick }: { item: DoneItem; onClick: () => void }) {
  return (
    <tr
      className="border-t border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={onClick}
    >
      <td className="px-4 py-3">
        <p className="font-medium text-gray-900">{item.supplier_name}</p>
        <p className="text-xs text-gray-400">{item.purchase_order_number}</p>
      </td>
      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-700">
        {relativeTime(item.received)}
      </td>
      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-700">
        {item.purchaser || <span className="text-gray-400">—</span>}
      </td>
      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-700">
        {item.validated_by.length > 0
          ? item.validated_by.map(validatorName).join(', ')
          : <span className="text-gray-400">—</span>}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {item.number_of_rows_to_confirm > 0
          ? `${item.number_of_confirmed_rows}/${item.number_of_rows_to_confirm} rows`
          : <span className="text-gray-400">—</span>}
      </td>
    </tr>
  )
}

export function DonePage() {
  const { workflowId } = useParams<{ workflowId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { onPurchaserOptions } = useOutletContext<OverviewOutletContext>()

  const page = Number(searchParams.get('page')) || 1
  const selectedSuppliers = searchParams.getAll('supplier')
  const selectedPurchasers = searchParams.getAll('purchaser')
  const searchQuery = searchParams.get('q') ?? ''

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['workflow-done', workflowId, page, selectedSuppliers, selectedPurchasers, searchQuery],
    queryFn: () =>
      getWorkflowDone(workflowId!, {
        page,
        suppliers: selectedSuppliers.length > 0 ? selectedSuppliers : undefined,
        purchasers: selectedPurchasers.length > 0 ? selectedPurchasers : undefined,
        q: searchQuery || undefined,
      }),
    enabled: !!workflowId,
    placeholderData: keepPreviousData,
    ...workflowListQueryOptions,
  })

  const purchaserFilters = data?.purchaser_filters
  useEffect(() => {
    onPurchaserOptions(purchaserFilters ?? [])
  }, [purchaserFilters, onPurchaserOptions])

  const isBackgroundRefreshing = isFetching && !isLoading && !!data
  const items = data?.data ?? []
  const hasAnyFilter = selectedSuppliers.length > 0 || selectedPurchasers.length > 0 || !!searchQuery

  function handlePage(p: number) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (p > 1) next.set('page', String(p))
        else next.delete('page')
        return next
      },
      { replace: true }
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">
        Loading…
      </div>
    )
  }

  if (items.length === 0 && !hasAnyFilter) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">
        No fully confirmed purchase orders yet
      </div>
    )
  }

  return (
    <div className="relative">
      <ListRefreshIndicator visible={isBackgroundRefreshing} />

      <div className="data-sheet">
      <table className="w-full text-left">
        <thead>
          <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 pb-2 pt-3">Supplier / Reference</th>
            <th className="hidden md:table-cell px-4 pb-2 pt-3">Received</th>
            <th className="hidden md:table-cell px-4 pb-2 pt-3">Purchaser</th>
            <th className="hidden md:table-cell px-4 pb-2 pt-3">Validated By</th>
            <th className="px-4 pb-2 pt-3">Progress</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">
                No results for current filters
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <Row
                key={item.id}
                item={item}
                onClick={() => navigate(`/workflow/${workflowId}/purchase-order/${item.id}`)}
              />
            ))
          )}
        </tbody>
      </table>
      </div>

      {data && (
        <Pagination
          page={data.page}
          totalCount={data.total}
          pageSize={data.page_size}
          onPage={handlePage}
        />
      )}
    </div>
  )
}
